// ============================================================
// OTel Exporter — Invia metriche, span / traces e log al proxy
// C# in formato OTLP JSON (HTTP JSON, non protobuf).
//
// Endpoint per ambiente (REACT_APP_OTLP_URL nei file .env.*):
//   dev/collaudo: https://compass-col.int.compassonline.it/otlp-collector/otlp
//   produzione:   https://www.compass.it/otlp-collector/otlp
// ============================================================

const OTLP_BASE = (() => {
    // Endpoint configurabile per ambiente: su famps l'host di produzione non e'
    // compass.it, quindi il same-origin non si puo' dedurre dall'hostname come su
    // fa3.2. REACT_APP_OTLP_URL ha la precedenza; i fallback restano quelli di fa3.2.
    const configured = process.env.REACT_APP_OTLP_URL;
    if (configured) return configured;

    const hostname = window.location.hostname;
    const isDev = hostname === "localhost" || hostname === "127.0.0.1" || /^172\./.test(hostname);
    if (isDev) {
        // In locale chiama direttamente il proxy di collaudo via HTTPS
        return "https://compass-col.int.compassonline.it/otlp-collector/otlp";
    }
    // Fallback se la variabile manca: stesso host (same-origin, niente CORS)
    return "/otlp-collector/otlp";
})();

// -- Identita del servizio --
// Unico punto in cui si dichiara il servizio: i resource attribute OTLP e i
// nomi degli scope derivano tutti da qui. Portando questa libreria su un
// altro clone di FA basta cambiare queste due costanti.
// Il nome del servizio distingue anche l'ambiente: 'famps' e' solo la
// produzione, il collaudo emette come 'fampscollaudo'. Le dashboard di
// produzione filtrano su service_name="famps" con match esatto, quindi i dati
// di collaudo non possono finirci dentro.
// Il default e' l'ambiente NON di produzione: se la variabile manca, i dati
// finiscono in collaudo anziche' inquinare le dashboard di produzione.
const SERVICE_NAME = process.env.REACT_APP_OTEL_SERVICE_NAME || "fampscollaudo";
const SERVICE_VERSION = process.env.REACT_APP_OTEL_SERVICE_VERSION || "1.0";
const SCOPE_JOURNEY = `${SERVICE_NAME}.journey`;
const SCOPE_MONITOR = `${SERVICE_NAME}.monitor`;

// Status OTLP degli span
const STATUS_UNSET = 0;
const STATUS_OK = 1;
const STATUS_ERROR = 2;

function getQuartoDOra() {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = Math.floor(now.getMinutes() / 15) * 15;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
}

function getMediacode() {
    return localStorage.getItem("newmediacode") || "";
}

// Valore della label mediacode su log e span: "vuoto" al posto della stringa vuota
function getMediacodeLabel() {
    return getMediacode() || "vuoto";
}

// Stack trace troppo lunghi gonfiano inutilmente il payload OTLP
function truncateStack(stack) {
    if (!stack) return undefined;
    return String(stack).slice(0, 2000);
}

// Punto esatto dell'eccezione (file, riga, colonna) fornito da window.onerror.
// È l'unica informazione che identifica gli script iniettati a runtime (tag
// manager, vendor): nei loro stack compare l'URL del documento, non del file,
// quindi senza questi campi non si risale al codice che ha lanciato l'errore.
function buildErrorLocation(errorDetails) {
    const details = errorDetails || {};
    const location = {};
    if (details.filename) location.filename = String(details.filename);
    if (details.lineno !== undefined && details.lineno !== null) location.lineno = details.lineno;
    if (details.colno !== undefined && details.colno !== null) location.colno = details.colno;
    if (details.errorName) location.error_name = String(details.errorName);
    return location;
}

// ── OTLP JSON helpers ──

function otlpTimestamp() {
    return Date.now() * 1000000; // nanosecondi
}

function toOtlpAttributes(attributes = {}) {
    return Object.entries(attributes).map(([key, value]) => ({
        key,
        value: { stringValue: String(value) },
    }));
}

// ── Generazione ID OTel ──

function generateId(bytes) {
    const arr = new Uint8Array(bytes);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

function generateTraceId() {
    return generateId(16); // 16 bytes = 128 bit
}

function generateSpanId() {
    return generateId(8); // 8 byte = 64 bit
}

// ── Metriche cumulative ──
// Grafana Cloud/Mimir rifiuta DELTA in ingest (400) e il proxy C# non aggrega,
// quindi le metriche vanno inviate CUMULATIVE gia' dal browser: ogni caricamento
// della pagina e' un'istanza (service.instance.id) che accumula in memoria i
// propri contatori e istogrammi a partire da uno startTime fisso.
// Niente label che cambiano nel tempo (es. quarto_dora): ogni valore nuovo aprirebbe
// una serie nuova con un solo campione. Le fasce orarie si ricavano dal timestamp.
// Limite: una serie per caricamento ha pochi campioni, quindi increase()/rate()
// perdono il primo incremento (o tutto, con un solo campione) se Mimir non
// inserisce lo zero allo startTime. Per questo i conteggi di eventi rari
// (landing, errori, path) si fanno sui log: vedi docs/OTEL.md.

const METRICS_INSTANCE_ID = generateId(8);
const METRICS_START_TIME = otlpTimestamp();
const HISTOGRAM_BOUNDS = [1000, 3000, 5000, 10000, 30000]; // + bucket +Inf
const cumulativeState = new Map();

function getCumulativeState(metricName, attributes, createInitialState) {
    const key = `${metricName}|${JSON.stringify(attributes)}`;
    if (!cumulativeState.has(key)) cumulativeState.set(key, createInitialState());
    return cumulativeState.get(key);
}

// Indice del bucket (prev < value <= bound); l'ultimo e' il bucket +Inf
function getHistogramBucketIndex(value) {
    const index = HISTOGRAM_BOUNDS.findIndex((bound) => value <= bound);
    return index === -1 ? HISTOGRAM_BOUNDS.length : index;
}

function buildScopeMetrics(scopeName, metrics) {
    return {
        scope: { name: scopeName },
        metrics,
    };
}

function buildCounter(name, increment, attributes = {}) {
    const state = getCumulativeState(name, attributes, () => ({ total: 0 }));
    state.total += increment;

    return {
        name,
        description: "",
        unit: "1",
        sum: {
            dataPoints: [
                {
                    attributes: toOtlpAttributes(attributes),
                    startTimeUnixNano: METRICS_START_TIME,
                    timeUnixNano: otlpTimestamp(),
                    asDouble: state.total,
                },
            ],
            aggregationTemporality: 2, // CUMULATIVE
            isMonotonic: true,
        },
    };
}

function buildHistogram(name, value, attributes = {}) {
    const state = getCumulativeState(name, attributes, () => ({
        count: 0,
        sum: 0,
        bucketCounts: new Array(HISTOGRAM_BOUNDS.length + 1).fill(0),
    }));
    state.count += 1;
    state.sum += value;
    state.bucketCounts[getHistogramBucketIndex(value)] += 1;

    return {
        name,
        description: "",
        unit: "ms",
        histogram: {
            dataPoints: [
                {
                    attributes: toOtlpAttributes(attributes),
                    startTimeUnixNano: METRICS_START_TIME,
                    timeUnixNano: otlpTimestamp(),
                    count: state.count,
                    sum: state.sum,
                    bucketCounts: [...state.bucketCounts],
                    explicitBounds: HISTOGRAM_BOUNDS,
                },
            ],
            aggregationTemporality: 2, // CUMULATIVE
        },
    };
}

// ── Log OTLP helpers ──

function buildLogRecord(severityText, body, attributes = {}) {
    const severityMap = { INFO: 9, WARN: 13, ERROR: 17 };
    return {
        timeUnixNano: otlpTimestamp(),
        severityNumber: severityMap[severityText] || 9,
        severityText,
        body: { stringValue: body },
        attributes: toOtlpAttributes(attributes),
    };
}

// ── Context Holder (JavaScript puro, non React) ──
// Tiene traccia dello span attivo per linkare gli span figli.
// Il sessionId identifica la sessione utente univoca.

const context = {
    traceId: null,
    sessionId: null,
    spanStack: [], // stack degli span attivi (ultimo = parent corrente)
    openPageSpans: new Map(), // spanId -> { pageName, startTimeNano, parentSpanId }
    path: [], // pagine visitate nella sessione, in ordine
};

function initSessionContext() {
    context.traceId = generateTraceId();
    context.sessionId = generateId(8);
    context.spanStack = [];
    context.openPageSpans = new Map();
    context.path = [];
    return { traceId: context.traceId, sessionId: context.sessionId };
}

function pushSpanContext(spanId) {
    context.spanStack.push(spanId);
}

function removeSpanContext(spanId) {
    context.spanStack = context.spanStack.filter((activeSpanId) => activeSpanId !== spanId);
}

function getParentSpanId() {
    return context.spanStack.length > 0 ? context.spanStack[context.spanStack.length - 1] : undefined;
}

function hasActiveContext() {
    return !!context.traceId;
}

function getSessionId() {
    return context.sessionId;
}

// ── Costruttori span OTLP ──

function buildSpanData(spanName, { startTimeNano, endTimeNano, attributes = {}, status = {}, spanId, parentSpanId = getParentSpanId() }) {
    return {
        spanId: spanId || generateSpanId(),
        traceId: context.traceId,
        parentSpanId: parentSpanId || undefined, // null = span radice
        name: spanName,
        kind: 1, // INTERNAL
        startTimeUnixNano: startTimeNano,
        endTimeUnixNano: endTimeNano,
        attributes: toOtlpAttributes(attributes),
        status: status.code ? { code: status.code, message: status.message || "" } : { code: STATUS_UNSET },
    };
}

// ── Export functions ──
// Tutti gli invii sono fire-and-forget: postOtlp non rilancia mai, i chiamanti
// non attendono la risposta del collector e l'app non ne resta bloccata.

// Attivo solo durante il flush alla chiusura della pagina: keepalive fa
// sopravvivere la richiesta all'unload, ma ha una quota di 64KB per documento,
// quindi non va usato per il traffico normale.
let isFlushingOnPageHide = false;

async function postOtlp(signalPath, payload) {
    try {
        const response = await fetch(`${OTLP_BASE}/${signalPath}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: isFlushingOnPageHide,
        });
        if (!response.ok) console.warn(`[otel] ${signalPath} push failed:`, response.status);
        return response.ok;
    } catch (error) {
        // silenzioso, non deve rompere l'app
        console.debug(`[otel] ${signalPath} push error:`, error.message);
        return false;
    }
}

function pushTraces(spans) {
    if (!spans || spans.length === 0) return;

    postOtlp("v1/traces", {
        resourceSpans: [
            {
                resource: {
                    attributes: toOtlpAttributes({
                        "service.name": SERVICE_NAME,
                        "service.version": SERVICE_VERSION,
                        "session.id": context.sessionId || "",
                    }),
                },
                scopeSpans: [
                    {
                        scope: { name: SCOPE_JOURNEY },
                        spans,
                    },
                ],
            },
        ],
    });
}

function pushMetrics(metrics) {
    postOtlp("v1/metrics", {
        resourceMetrics: [
            {
                resource: {
                    attributes: toOtlpAttributes({
                        "service.name": SERVICE_NAME,
                        "service.version": SERVICE_VERSION,
                        "service.instance.id": METRICS_INSTANCE_ID,
                    }),
                },
                scopeMetrics: [buildScopeMetrics(SCOPE_MONITOR, metrics)],
            },
        ],
    });
}

function pushLogs(logRecords) {
    postOtlp("v1/logs", {
        resourceLogs: [
            {
                resource: {
                    attributes: toOtlpAttributes({ "service.name": SERVICE_NAME }),
                },
                scopeLogs: [
                    {
                        scope: { name: SCOPE_MONITOR },
                        logRecords,
                    },
                ],
            },
        ],
    });
}

// ── Metriche specifiche del progetto ──

let landingSent = false;

function trackLanding() {
    if (landingSent) return;
    landingSent = true;

    const mediacode = getMediacodeLabel();
    pushMetrics([buildCounter("fa.landing.users", 1, { mediacode })]);
    pushLogs([buildLogRecord("INFO", "user_landed", { mediacode, quarto_dora: getQuartoDOra(), event: "landing" })]);
}

function trackComponentView(pageName) {
    pushMetrics([buildCounter("fa.component.viewed", 1, { page: pageName, mediacode: getMediacode() })]);
}

// Tempo di permanenza dell'utente sul componente (mount -> unmount/navigazione),
// non tempo di caricamento: va inviato alla chiusura della pagina, non al mount.
function trackComponentDuration(pageName, durationMs) {
    pushMetrics([buildHistogram("fa.component.duration_ms", durationMs, { page: pageName })]);
}

// Percorso di navigazione: un log per cambio pagina con la pagina corrente
// e il path cumulato fino a quel punto (session.id, ...). Non sostituisce
// la view/lo span di pagina, si affianca: chiusura sessione e creazione
// appuntamento sono anchor troppo rari/inaffidabili per ricostruire il
// journey, quindi il path va aggiornato ad ogni cambio pagina, che invece
// scatta sempre. L'ultimo log ricevuto per una session.id è il path finale
// (o il punto di abbandono, se l'utente droppa).
function trackPagePath(pageName) {
    context.path.push(pageName);

    pushLogs([
        buildLogRecord("INFO", "page_path", {
            "session.id": context.sessionId || "",
            mediacode: getMediacodeLabel(),
            quarto_dora: getQuartoDOra(),
            event: "page_path",
            page: pageName,
            path: context.path.join(","),
        }),
    ]);
}

function trackComponentError(pageName, errorMessage, errorStack) {
    const quartoDora = getQuartoDOra();
    const stack = truncateStack(errorStack);

    pushMetrics([buildCounter("fa.component.error", 1, { page: pageName })]);
    pushLogs([
        buildLogRecord("ERROR", `component_error: ${errorMessage}`, {
            page: pageName,
            quarto_dora: quartoDora,
            event: "component_error",
            ...(stack ? { stack } : {}),
        }),
    ]);
}

function trackCreateAppointment() {
    pushLogs([
        buildLogRecord("INFO", "create_appointment_called", {
            mediacode: getMediacodeLabel(),
            quarto_dora: getQuartoDOra(),
            event: "create_appointment",
        }),
    ]);
}

// ── Tracking chiamate ai servizi (durata media) ──

async function trackServiceCall(serviceName, promise) {
    const start = Date.now();
    const quartoDora = getQuartoDOra();

    try {
        const result = await promise;

        pushMetrics([
            buildCounter("fa.service.called", 1, { service: serviceName }),
            buildHistogram("fa.service.duration_ms", Date.now() - start, { service: serviceName }),
        ]);

        return result;
    } catch (error) {
        const errorMessage = (error && error.message) || String(error);
        const stack = truncateStack(error && error.stack);

        pushMetrics([
            buildCounter("fa.service.error", 1, { service: serviceName }),
            buildHistogram("fa.service.duration_ms", Date.now() - start, { service: serviceName }),
        ]);
        pushLogs([
            buildLogRecord("ERROR", `service_error: ${errorMessage}`, {
                service: serviceName,
                quarto_dora: quartoDora,
                event: "service_error",
                ...(stack ? { stack } : {}),
            }),
        ]);

        throw error;
    }
}

// ── Tracking errori JS non gestiti (window.onerror / unhandledrejection) ──

function trackGlobalError(errorMessage, source = "window", errorStack, errorDetails) {
    const quartoDora = getQuartoDOra();
    const stack = truncateStack(errorStack);
    const location = buildErrorLocation(errorDetails);

    pushMetrics([buildCounter("fa.global.error", 1, { source })]);
    pushLogs([
        buildLogRecord("ERROR", `global_error: ${errorMessage}`, {
            source,
            quarto_dora: quartoDora,
            event: "global_error",
            ...(stack ? { stack } : {}),
            ...location,
        }),
    ]);

    if (hasActiveContext()) {
        const now = otlpTimestamp();
        pushTraces([
            buildSpanData("error:global", {
                startTimeNano: now,
                endTimeNano: now,
                attributes: {
                    source,
                    error: errorMessage,
                    "session.id": context.sessionId,
                    ...(stack ? { stack } : {}),
                    ...location,
                },
                status: { code: STATUS_ERROR, message: errorMessage },
            }),
        ]);
    }
}

// ── Funzioni span per journey utente ──

let sessionSpan = null; // { spanId, startTimeNano } dello span radice aperto
let sessionEnded = false;

function buildSessionAttributes() {
    return {
        "session.id": context.sessionId,
        mediacode: getMediacodeLabel(),
        quarto_dora: getQuartoDOra(),
        component: "app",
    };
}

function trackSpanSessionStart() {
    const { traceId, sessionId } = initSessionContext();
    const now = otlpTimestamp();

    sessionEnded = false;

    // Inviato subito con endTime = startTime (status UNSET, sessione aperta): cosi'
    // la sessione viene contata anche se l'utente chiude senza che arrivi la
    // chiusura. trackSpanSessionEnd lo reinvia con lo stesso spanId e la durata reale.
    const span = buildSpanData("session", {
        startTimeNano: now,
        endTimeNano: now,
        attributes: buildSessionAttributes(),
    });
    sessionSpan = { spanId: span.spanId, startTimeNano: now };

    // Registra come span corrente
    pushSpanContext(span.spanId);
    pushTraces([span]);

    return { traceId, sessionId };
}

function trackSpanPageStart(pageName) {
    if (!hasActiveContext()) return null;

    const spanId = generateSpanId();
    context.openPageSpans.set(spanId, {
        pageName,
        startTimeNano: otlpTimestamp(),
        parentSpanId: getParentSpanId() || null,
    });
    pushSpanContext(spanId);

    return spanId;
}

// Chiude lo span di pagina aperto da trackSpanPageStart. No-op se lo span e'
// gia' stato chiuso (es. insieme alla sessione) o se la sessione e' stata
// reinizializzata nel frattempo.
function trackSpanPageEnd(spanId, attributes = {}) {
    const openPage = context.openPageSpans.get(spanId);
    if (!openPage) return;

    context.openPageSpans.delete(spanId);
    removeSpanContext(spanId);

    pushTraces([
        buildSpanData(`page:${openPage.pageName}`, {
            // riusa lo spanId aperto, cosi' gli span figli creati mentre la pagina
            // era montata puntano al parent giusto
            spanId,
            parentSpanId: openPage.parentSpanId,
            startTimeNano: openPage.startTimeNano,
            endTimeNano: otlpTimestamp(),
            attributes: {
                page: openPage.pageName,
                mediacode: getMediacodeLabel(),
                quarto_dora: getQuartoDOra(),
                "session.id": context.sessionId,
                ...attributes,
            },
            status: { code: STATUS_OK },
        }),
    ]);
}

// Chiude la sessione insieme agli span di pagina ancora aperti (es. riepilogo,
// che resta montato dopo la fine del journey): nessuno span figlio deve
// terminare dopo lo span radice.
function trackSpanSessionEnd() {
    if (!sessionSpan || !hasActiveContext() || sessionEnded) return;
    sessionEnded = true;

    Array.from(context.openPageSpans.keys()).forEach((spanId) => trackSpanPageEnd(spanId));

    pushTraces([
        buildSpanData("session", {
            spanId: sessionSpan.spanId,
            parentSpanId: null,
            startTimeNano: sessionSpan.startTimeNano,
            endTimeNano: otlpTimestamp(),
            attributes: { ...buildSessionAttributes(), completed: "true" },
            status: { code: STATUS_OK },
        }),
    ]);

    removeSpanContext(sessionSpan.spanId);
    sessionSpan = null;
}

// Chiusura della tab / navigazione fuori dall'app: nessun unmount React arriva,
// quindi chiude qui la sessione e la pagina corrente. Se la pagina entra nella
// back/forward cache (persisted) non chiude nulla: l'utente puo' tornare
// indietro e il journey continua nella stessa sessione. Se la pagina viene poi
// scartata dalla cache, la sessione resta con il solo span di apertura.
function trackPageHide(event) {
    if (event && event.persisted) return;

    isFlushingOnPageHide = true;
    trackSpanSessionEnd();
    isFlushingOnPageHide = false;
}

function trackSpanPageError(pageName, errorMessage, errorStack) {
    if (!hasActiveContext()) return;

    const now = otlpTimestamp();
    const stack = truncateStack(errorStack);

    // Errore asincrono durante la pagina — non chiude lo span ma logga
    pushTraces([
        buildSpanData(`error:${pageName}`, {
            startTimeNano: now,
            endTimeNano: now,
            attributes: {
                page: pageName,
                error: errorMessage,
                "session.id": context.sessionId,
                ...(stack ? { stack } : {}),
            },
            status: { code: STATUS_ERROR, message: errorMessage },
        }),
    ]);
}

function trackSpanCreateAppointment() {
    if (!hasActiveContext()) return;

    const now = otlpTimestamp();

    pushTraces([
        buildSpanData("create:appointment", {
            startTimeNano: now,
            endTimeNano: now,
            attributes: {
                mediacode: getMediacodeLabel(),
                quarto_dora: getQuartoDOra(),
                "session.id": context.sessionId,
                event: "create_appointment",
            },
            status: { code: STATUS_OK },
        }),
    ]);
}

export {
    trackLanding,
    trackComponentView,
    trackComponentDuration,
    trackComponentError,
    trackPagePath,
    trackCreateAppointment,
    pushMetrics,
    pushLogs,
    OTLP_BASE,
    // Journey con span
    trackSpanSessionStart,
    trackSpanSessionEnd,
    trackSpanPageStart,
    trackSpanPageEnd,
    trackPageHide,
    trackSpanPageError,
    trackSpanCreateAppointment,
    getSessionId,
    hasActiveContext,
    // Durata media chiamate ai servizi
    trackServiceCall,
    // Errori JS non gestiti
    trackGlobalError,
};
